import {Component, HostListener, OnInit, ViewChild} from '@angular/core';
import {BehaviorSubject, Subscription} from 'rxjs';
import {LegalDocument} from 'src/app/models/legal-document';
import {LegalDocumentsService} from 'src/app/services/legal-documents.service';
import {UiService} from 'src/app/services/ui.service';
import {AppComponent} from '../../app.component';
import {CanDeactivateComponent} from '../../guards/can-deactivate-guard';
import {environment} from '../../../environments/environment';
import {EnvironmentService} from "../../services/environment.service";

declare var webOS: any;

@Component({
  selector: 'app-settings-page',
  templateUrl: './settings-page.component.html',
  styleUrls: ['./settings-page.component.scss']
})
export class SettingsPageComponent implements OnInit, CanDeactivateComponent {

  selectedOption = undefined;
  selectedDocument: LegalDocument = undefined;
  version: string;
  useragent: string;
  webOsVersion$: BehaviorSubject<string> = new BehaviorSubject(undefined);
  currentPlatform$: BehaviorSubject<string> = new BehaviorSubject(undefined);
  chromiumVersion$: BehaviorSubject<string> = new BehaviorSubject(undefined);

  @ViewChild('scrollField') scrollField;
  @ViewChild('firstOption') firstOption;

  options = [
    {
      label: 'Account',
      tag: 'account',
      isDocument: false,
      active: false,
      start: true
    },
    {
      label: 'Terms & Conditions',
      tag: 'terms-and-conditions',
      isDocument: true,
      active: false
    },
    {
      label: 'Privacy Policy',
      tag: 'privacy-policy',
      isDocument: true,
      active: false
    },
    {
      label: 'Refund Policy',
      tag: 'refund-policy',
      isDocument: true,
      active: false
    },
    {
      label: 'Log Out',
      tag: 'logout',
      isDocument: false,
      active: false
    },
  ];
  private documentHttp: Subscription;

  get host() {
    return environment.host
  }

  constructor(
    private environmentService: EnvironmentService,
    private legalService: LegalDocumentsService,
    private ui: UiService,
    private app: AppComponent) {
  }

  async ngOnInit() {
    this.version = this.app.version;
    await this.selectOption('account');

    this.getWebOSVersion();
    this.getPlatform();
    this.getChromiumVersion();
  }

  getWebOSVersion() {
    if (webOS) {
      webOS.deviceInfo((device => {
        setTimeout(() => {
          if (device.version && device.version !== 'undefined' && device.version !== undefined) {
            this.webOsVersion$.next(device.version + ' | ' + device.sdkVersion);
          }
        })
      }));
    }
  }

  getPlatform() {
    let platform = this.environmentService.platform();
    if (platform === "smart-tv") {
      platform = "lg";
    }
    this.currentPlatform$.next(platform);
  }

  getChromiumVersion() {
    const userAgent = navigator.userAgent;

    // Extract Chromium/Chrome version
    const chromiumMatch = userAgent.match(/Chrome\/([0-9]+\.[0-9]+\.[0-9]+\.[0-9]+)/);
    if (chromiumMatch) {
      this.chromiumVersion$.next(chromiumMatch[1]);
      return;
    }

    // For Edge (Chromium-based)
    const edgeMatch = userAgent.match(/Edg\/([0-9]+\.[0-9]+\.[0-9]+\.[0-9]+)/);
    if (edgeMatch) {
      this.chromiumVersion$.next(`Edge ${edgeMatch[1]}`);
      return;
    }

    // Alternative method using feature detection
    if ((window as any).chrome) {
      this.chromiumVersion$.next('Chromium-based');
    }
  }

  canDeactivate(): boolean {
    if (this.ui.isModalOpen) {
      this.ui.stopShowingGenericModal();
      return false;
    }
    return true;
  }

  focusOption = () => {
    this.firstOption.nativeElement.focus();
  }

  showLogOutModal() {
    this.ui.showLogOutModal(this.focusOption);
  }

  async selectOption(option: string) {
    if (this.documentHttp) {
      this.documentHttp.unsubscribe();
      this.documentHttp = undefined;
    }

    if (option == 'logout') {
      this.showLogOutModal();
      return;
    }

    this.options.forEach(x => {
      x.active = false;
    });

    this.selectedOption = this.options.find(x => x.tag == option);

    if (this.selectedOption.isDocument) {
      this.selectedOption.active = true;
      await this.getDocument(this.selectedOption.tag);
    }

    this.scrollTop();
  }

  async getDocument(document: string): Promise<void> {
    this.documentHttp = this.legalService.getLatestDocumentByType(document).subscribe(doc => {
      this.selectedDocument = doc;
    })
  }

  scrollTop() {
    const el = this.scrollField?.nativeElement;
    if (el) {
      el.scrollTop = 0;
    }
  }

  protected readonly navigator = navigator;

  scrollDown() {
    const el = this.scrollField?.nativeElement;
    if (el) {
      el.scrollTop = el.scrollTop + 100;
    }
  }

  scrollUp() {
    const el = this.scrollField?.nativeElement;
    if (el) {
      el.scrollTop = el.scrollTop - 100;
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: any) {
    let key = event.key;
    if (key === 'Unidentified' && event.keyCode == 204) {
      this.scrollDown();
    }
    if (key === 'Unidentified' && event.keyCode == 203) {
      this.scrollUp();
    }
  }
}
